/**
 * slider-ingredients.js
 *
 * Sticky scroll reveal + card slider.
 * Scroll container: .c-si__cards-area
 * Navigation: invisible prev/next buttons ([data-si-prev] / [data-si-next])
 *   that call scrollBy() — most reliable cross-browser approach.
 */

class SliderIngredientsSection extends HTMLElement {
  constructor() {
    super();
    this._onScroll = this._onScroll.bind(this);
    this._onSectionLoad = this._onSectionLoad.bind(this);
    this._cleanupFns = [];
    this._rafId = null;
    this._observer = null;
  }

  connectedCallback() {
    this._reveal = this.querySelector('[data-si-reveal]');
    this._scroller = this.querySelector('.c-si__cards-area');
    this._prev = this.querySelector('[data-si-prev]');
    this._next = this.querySelector('[data-si-next]');

    this._init();

    document.addEventListener('shopify:section:load', this._onSectionLoad);
  }

  disconnectedCallback() {
    document.removeEventListener('shopify:section:load', this._onSectionLoad);
    this._teardown();
  }

  _onSectionLoad(e) {
    if (e.target && e.target.contains(this)) {
      this._teardown();
      this._init();
    }
  }

  // ─── Scroll reveal (sticky lane) ────────────────────────────────────────────

  _init() {
    if (this._reveal) {
      this._reveal.style.setProperty('--si-line2-opacity', '0');
    }

    const lane = this.parentElement || this;

    this._observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        window.addEventListener('scroll', this._onScroll, { passive: true });
        this._tick();
      } else {
        window.removeEventListener('scroll', this._onScroll);
        this._tick();
      }
    }, { threshold: 0 });

    this._observer.observe(lane);

    // Use GSAP scroll-triggered effects if available, but also keep manual slider.
    if (window.innerWidth >= 1024) {
      this._initGSAP();
    }

    if (this._scroller) {
      this._initGSAP();
    }
  }

  _initGSAP() {
    if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') {
      console.warn('GSAP or ScrollTrigger not found');
      return;
    }

    gsap.registerPlugin(ScrollTrigger);

    const cardsTrack = this.querySelector('[data-si-track]');
    const cards = this.querySelectorAll('.c-si__card');
    const lane = this.parentElement || this;

    if (!cardsTrack) return;

    // Total horizontal distance to scroll
    const availableWidth = this._scroller.clientWidth;
    const trackWidth = cardsTrack.scrollWidth;
    const maxScroll = Math.max(0, trackWidth - availableWidth);

    if (maxScroll <= 0) {
      // If content fits, center it and don't animate scroll
      this._scroller.style.justifyContent = 'center';
    } else {
      // Ensure flex-start for slider mode
      this._scroller.style.justifyContent = 'flex-start';
      
      // Animate the actual scroll of the track via transform
      gsap.fromTo(cardsTrack,
        { x: 0 },
        {
          x: -maxScroll,
          ease: 'none',
          scrollTrigger: {
            trigger: lane,
            start: 'top 15%',
            end: 'bottom 60%',
            scrub: 2
          }
        }
      );
    }

    // Complementary entrance animations for cards
    const cardsTl = gsap.timeline({
      scrollTrigger: {
        trigger: lane,
        start: 'top 85%',
        end: 'bottom 40%',
        scrub: 2
      }
    });

    cards.forEach((card, index) => {
      cardsTl.fromTo(card, {
        opacity: 0,
        x: 60
      }, {
        opacity: 1,
        x: 0,
        duration: 2,
        ease: 'power1.out'
      }, index * 0.4);
    });

    this._cleanupFns.push(() => {
      ScrollTrigger.getAll().forEach(st => {
        if (st.vars.trigger === lane) st.kill();
      });
    });
  }

  _teardown() {
    window.removeEventListener('scroll', this._onScroll);
    if (this._observer) { this._observer.disconnect(); this._observer = null; }
    if (this._rafId) { cancelAnimationFrame(this._rafId); this._rafId = null; }
    this._cleanupFns.forEach((cleanup) => cleanup());
    this._cleanupFns = [];
    if (this._reveal) {
      this._reveal.style.setProperty('--si-line1-opacity', '1');
      this._reveal.style.setProperty('--si-line2-opacity', '0');
    }
  }

  _onScroll() {
    if (this._rafId) return;
    this._rafId = requestAnimationFrame(() => { this._rafId = null; this._tick(); });
  }

  _tick() {
    if (!this._reveal) return;
    const lane = this.parentElement || this;
    const rect = lane.getBoundingClientRect();
    const winH = window.innerHeight || document.documentElement.clientHeight;

    // Start fading in when element top is at 80% of window
    // Fully faded in when element top is at 40% of window
    const startPoint = winH * 0.8;
    const endPoint = winH * 0.4;
    const scrolledPx = startPoint - rect.top;

    // line1 always fully visible
    this._reveal.style.setProperty('--si-line1-opacity', '1');

    if (scrolledPx <= 0) {
      this._reveal.style.setProperty('--si-line2-opacity', '0.3');
      return;
    }

    // line2: 0.3 → 1 as scroll progresses (0 → 1)
    const distance = startPoint - endPoint;
    const p = Math.max(0, Math.min(1, scrolledPx / distance));
    const line2 = (0.3 + p * 0.7).toFixed(4);

    this._reveal.style.setProperty('--si-line2-opacity', line2);
  }
}

if (!customElements.get('slider-ingredients-section')) {
  customElements.define('slider-ingredients-section', SliderIngredientsSection);
}
